import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { PublicLayout, Section, Eyebrow } from "@/components/PublicLayout";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { ArrowRight, CheckCircle2 } from "lucide-react";

const T = {
  en: {
    eyebrow: "Cognitive Assessment Centre", title: "How you think matters most.",
    intro: "You'll answer one question at a time. There are no trick scores — we care about your reasoning, how you explain it, and how you meet unfamiliar problems. Take your time. No emotion or biometric inference is ever used.",
    begin: "Begin the assessment", question: "Question", of: "of",
    choose: "Choose your answer", your_answer: "Your answer", answer_ph: "Type your answer",
    explain: "Explain your reasoning", explain_ph: "How did you work it out? What made you sure — or unsure?",
    confident: "How confident are you?", submit: "Submit & continue", parent: "This task may be facilitated by a parent for younger applicants.",
    done_eyebrow: "Assessment complete", thanks: "Thank you.",
    done_body: "Your reasoning portrait has been recorded and will inform a human admissions decision. This is a summary — never an automated verdict.",
    readiness: "Reasoning strength", strongest: "Strongest domains", evidence: "Evidence confidence",
    profile: "Your reasoning portrait", home: "Return home",
    conf: [["Low", 1], ["Unsure", 2], ["Fairly sure", 3], ["Confident", 4], ["Certain", 5]],
  },
  fr: {
    eyebrow: "Centre d'évaluation cognitive", title: "Votre façon de penser compte avant tout.",
    intro: "Vous répondez à une question à la fois. Il n'y a pas de score piège — ce qui nous intéresse, c'est votre raisonnement, la façon dont vous l'expliquez et votre manière d'aborder des problèmes inconnus. Prenez votre temps. Aucune inférence émotionnelle ou biométrique n'est utilisée.",
    begin: "Commencer l'évaluation", question: "Question", of: "sur",
    choose: "Choisissez votre réponse", your_answer: "Votre réponse", answer_ph: "Saisissez votre réponse",
    explain: "Expliquez votre raisonnement", explain_ph: "Comment avez-vous procédé ? Qu'est-ce qui vous a rendu sûr — ou incertain ?",
    confident: "Quel est votre niveau de confiance ?", submit: "Valider et continuer", parent: "Cette tâche peut être accompagnée par un parent pour les plus jeunes.",
    done_eyebrow: "Évaluation terminée", thanks: "Merci.",
    done_body: "Votre portrait de raisonnement a été enregistré et éclairera une décision d'admission humaine. Ceci est un résumé — jamais un verdict automatisé.",
    readiness: "Force de raisonnement", strongest: "Domaines les plus forts", evidence: "Confiance des indices",
    profile: "Votre portrait de raisonnement", home: "Retour à l'accueil",
    conf: [["Faible", 1], ["Incertain", 2], ["Assez sûr", 3], ["Confiant", 4], ["Certain", 5]],
  },
};

export default function Assessment() {
  const { appId } = useParams();
  const nav = useNavigate();
  const { lang } = useI18n();
  const t = T[lang] || T.en;
  const [sid, setSid] = useState(null);
  const [state, setState] = useState("start");
  const [q, setQ] = useState(null);
  const [meta, setMeta] = useState({ index: 0, total: 8 });
  const [answer, setAnswer] = useState("");
  const [explanation, setExplanation] = useState("");
  const [confidence, setConfidence] = useState(null);
  const [report, setReport] = useState(null);
  const startTime = useRef(Date.now());

  const begin = async () => {
    const { data } = await api.post(`/assessment/${appId}/start`);
    setSid(data.session_id); setState("question"); loadNext(data.session_id);
  };
  const loadNext = async (sessionId) => {
    const { data } = await api.get(`/assessment/session/${sessionId}/next`);
    if (data.done) { complete(sessionId); return; }
    setQ(data.question); setMeta({ index: data.index, total: data.total });
    setAnswer(""); setExplanation(""); setConfidence(null); startTime.current = Date.now();
  };
  const submit = async () => {
    await api.post(`/assessment/session/${sid}/answer`, {
      answer, explanation, confidence, response_time_ms: Date.now() - startTime.current, revised: false, hint_requested: false,
    });
    loadNext(sid);
  };
  const complete = async (sessionId) => {
    const { data } = await api.post(`/assessment/session/${sessionId}/complete`);
    setReport(data); setState("done");
  };

  const canSubmit = (q?.options ? !!answer : (answer.trim() || explanation.trim())) && confidence;

  return (
    <PublicLayout>
      <Section className="py-12 md:py-16 max-w-3xl">
        {state === "start" && (
          <div>
            <Eyebrow>{t.eyebrow}</Eyebrow>
            <h1 className="font-serif text-4xl md:text-6xl tracking-tight text-ink">{t.title}</h1>
            <p className="mt-5 text-ink/65 leading-relaxed">{t.intro}</p>
            <button data-testid="assessment-begin" onClick={begin} className="mt-8 inline-flex items-center gap-2 bg-heraldic text-white px-8 py-3.5 rounded-sm hover:bg-heraldic-700 transition-colors">{t.begin} <ArrowRight className="h-4 w-4" /></button>
          </div>
        )}

        {state === "question" && q && (
          <div>
            <Progress value={(meta.index / meta.total) * 100} className="h-1" />
            <div className="mt-2 flex justify-between text-xs text-ink/40"><span>{t.question} {meta.index + 1} {t.of} {meta.total}</span><span className="uppercase">{q.domain}</span></div>
            <div className="mt-8 animate-fade-up" key={q.qid}>
              <div className="font-mono text-xs text-heraldic uppercase">{q.title}</div>
              <h2 className="mt-3 font-serif text-2xl md:text-3xl text-ink leading-snug">{q.text}</h2>
              {q.parent_facilitation && <p className="mt-3 text-xs text-heraldic">{t.parent}</p>}

              <div className="mt-6 space-y-4">
                {q.options ? (
                  <div>
                    <label className="text-sm text-ink/50">{t.choose}</label>
                    <div className="mt-2 grid gap-2">
                      {q.options.map(([key, label]) => (
                        <button key={key} data-testid={`mcq-option-${key}`} onClick={() => setAnswer(key)}
                          className={`text-left px-4 py-3 rounded-sm border transition-colors ${answer === key ? "bg-ink text-paper border-ink" : "border-border text-ink hover:border-heraldic"}`}>
                          <span className="font-mono text-xs mr-3 opacity-70">{key}</span>{label}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="text-sm text-ink/50">{t.your_answer}</label>
                    <Textarea data-testid="assessment-answer" rows={2} value={answer} onChange={(e) => setAnswer(e.target.value)} className="mt-1 rounded-sm" placeholder={t.answer_ph} />
                  </div>
                )}
                <div>
                  <label className="text-sm text-ink/50">{t.explain}</label>
                  <Textarea data-testid="assessment-explanation" rows={4} value={explanation} onChange={(e) => setExplanation(e.target.value)} className="mt-1 rounded-sm" placeholder={t.explain_ph} />
                </div>
                <div>
                  <label className="text-sm text-ink/50">{t.confident}</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {t.conf.map(([l, v]) => (
                      <button key={v} data-testid={`confidence-${v}`} onClick={() => setConfidence(v)} className={`px-3 py-1.5 text-sm rounded-sm border transition-colors ${confidence === v ? "bg-ink text-paper border-ink" : "border-border text-ink/60 hover:border-ink"}`}>{l}</button>
                    ))}
                  </div>
                </div>
              </div>
              <button data-testid="assessment-submit" disabled={!canSubmit} onClick={submit} className="mt-8 inline-flex items-center gap-2 bg-heraldic text-white px-7 py-3 rounded-sm hover:bg-heraldic-700 transition-colors disabled:opacity-40">{t.submit} <ArrowRight className="h-4 w-4" /></button>
            </div>
          </div>
        )}

        {state === "done" && report && (
          <div data-testid="assessment-report">
            <Eyebrow>{t.done_eyebrow}</Eyebrow>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-ink">{t.thanks}</h1>
            <p className="mt-4 text-ink/65 leading-relaxed">{t.done_body}</p>
            <div className="mt-8 grid sm:grid-cols-3 gap-px bg-border border border-border">
              <div className="bg-paper p-6"><div className="font-serif text-5xl text-heraldic">{report.overall_readiness}</div><div className="text-xs text-ink/50 mt-1">{t.readiness}</div></div>
              <div className="bg-paper p-6"><div className="font-serif text-2xl text-ink capitalize">{(report.strongest_domains || []).join(", ") || "—"}</div><div className="text-xs text-ink/50 mt-1">{t.strongest}</div></div>
              <div className="bg-paper p-6"><div className="font-serif text-2xl text-ink capitalize">{report.confidence}</div><div className="text-xs text-ink/50 mt-1">{t.evidence}</div></div>
            </div>
            <div className="mt-8">
              <div className="eyebrow text-ink/40 mb-3">{t.profile}</div>
              <div className="space-y-2">
                {Object.entries(report.reasoning_profile || {}).map(([d, sc]) => (
                  <div key={d} className="flex items-center gap-3">
                    <div className="w-40 text-sm text-ink/60 capitalize">{d}</div>
                    <div className="flex-1 h-2 bg-secondary rounded-sm overflow-hidden"><div className="h-full bg-heraldic" style={{ width: `${sc}%` }} /></div>
                    <div className="w-10 text-right font-mono text-xs text-ink/50">{sc}</div>
                  </div>
                ))}
              </div>
            </div>
            <p className="mt-6 text-xs text-ink/45">{report.note}</p>
            <button onClick={() => nav("/centre")} className="mt-8 bg-ink text-paper px-7 py-3 rounded-sm hover:bg-heraldic transition-colors">{t.home}</button>
          </div>
        )}
      </Section>
    </PublicLayout>
  );
}
